import * as uuid from 'uuid';
import { createTransformComponent } from '../v3d-components/transform-component';
import { Visualize3dComponent } from '../v3d-components/visualize3d-component';

export interface Visualize3dObject {
  children: Visualize3dObject[];
  components: Visualize3dComponent[];
  id: string;
  name: string;
}

/**
 * Create new Object3d
 */
export function createv3dObject(name: string): Visualize3dObject {
  return {
    id: uuid.v4(),
    name,
    components: [createTransformComponent()],
    children: [],
  };
}

/**
 * Add child to a parent
 * @param parent Parent Object3D
 * @param child  Child Object3D
 */
export function addChild(parent: Visualize3dObject, child: Visualize3dObject): void {
  parent.children.push(child);
}

/**
 * Remove child from a parent
 * @param parent Parent Object3D
 * @param child Child Object3D
 */
export function removeChild(parent: Visualize3dObject, child: Visualize3dObject): void {
  const index = parent.children.indexOf(child);
  if (index > -1) {
    parent.children.splice(index, 1);
  }
}

/**
 * Get a parent for an object3d in the tree. Null if parent is not found.
 * @param tree V3dObject3D tree
 * @param v3dObject V3dObject3D to look up the parent for
 */
export function getParent(tree: Visualize3dObject, v3dObject: Visualize3dObject): Visualize3dObject | null {
  return findInTree(tree, o => !!o.children.find(c => c === v3dObject));
}

/**
 * Traverse whole V3dObject3D tree depth-first while executing the func for each object in the tre
 * @param v3dObject V3dObject3d (root)
 * @param func Function to invoke on each descendant
 */
export function traverse(v3dObject: Visualize3dObject, func: (o: Visualize3dObject) => void): void {
  func(v3dObject);
  for (const child of v3dObject.children) {
    traverse(child, func);
  }
}

/**
 * Creates a deep clone of the V3dObject3D
 */
export function clone(v3dObject: Visualize3dObject): Visualize3dObject {
  const newV3dObject: Visualize3dObject = {
    id: v3dObject.id,
    name: v3dObject.name,
    components: JSON.parse(JSON.stringify(v3dObject.components)),
    children: v3dObject.children.map(c => clone(c)),
  };
  return newV3dObject;
}

/**
 * Add component to V3dObject3D
 */
export function addComponent<T extends Visualize3dComponent>(v3dObject: Visualize3dObject, component: T): void {
  v3dObject.components.push(component);
}

/**
 * Remove component from V3dObject3D
 */
export function removeComponent<T extends Visualize3dComponent>(v3dObject: Visualize3dObject, component: T): void {
  const index = v3dObject.components.indexOf(component);
  if (index < 0) {
    throw new Error(`Object3D ${v3dObject.name} [${v3dObject.id}] does not contain the provided component`);
  }
  v3dObject.components.splice(index, 1);
}

/**
 * Get the first component of matching type in the V3dObject3D
 * @param v3dObject V3dObject3D
 * @param type Component type
 */
export function getComponent<T extends Visualize3dComponent>(v3dObject: Visualize3dObject, type: string): T | undefined {
  const component = v3dObject.components.find(c => c.type === type) as T;
  if (component) {
    return component;
  }
  return undefined;
}

/**
 * Perform a dept-first search through the V3dObject3D hierarchy - starting from the parent object,
 * and return the first matching object in the tree. Null if match is not found.
 * @param v3dObject V3dObject3D
 * @param selector Selector function
 */
export function findInTree(v3dObject: Visualize3dObject, selector: (obj: Visualize3dObject) => boolean): Visualize3dObject | null {
  if (selector(v3dObject)) {
    return v3dObject;
  }
  for (const child of v3dObject.children) {
    const match = findInTree(child, selector);
    if (match) {
      return match;
    }
  }
  return null;
}
